<script setup lang="ts">
import type { ProductModelLookupItem } from '@/helpers/fetchers';
import type { JobEditProductItem } from '@/models/jobEdit';

import { computed } from 'vue';

import PickingStatusChip from '@/components/chips/PickingStatusChip.vue';
import KNumberField from '@/components/fields/KNumberField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import ProductModelLookup from '@/components/lookups/ProductModelLookup.vue';
import ProductPrefixSelect from '@/components/selects/ProductPrefixSelect.vue';
import {
  CUSTOM_PRODUCT_TYPE_CODES,
  createEmptyProductItem,
} from '@/models/jobEdit';

export interface Props {
  modelValue?: JobEditProductItem;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => createEmptyProductItem(),
  readonly: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: JobEditProductItem];
  delete: [event: Event];
}>();

function handlePrefixUpdate(value: string | undefined): void {
  emit('update:modelValue', { ...props.modelValue, prefix: value });
}

function handleModelUpdate(value: string): void {
  emit('update:modelValue', {
    ...props.modelValue,
    model: value,
    model_id: null,
  });
}

function handleModelSelectItem(item: ProductModelLookupItem): void {
  emit('update:modelValue', {
    ...props.modelValue,
    model: item.name,
    model_id: item.model_id,
    type_code: item.type_code,
    weight: item.weight || null,
    width: item.width || null,
    height: item.height || null,
    length: item.length || null,
  });
}

const dimensionsText = computed(() => {
  const p = props.modelValue;
  if (!p.type_code || !CUSTOM_PRODUCT_TYPE_CODES.includes(p.type_code)) {
    return '';
  }
  const parts: string[] = [];
  if (p.weight) parts.push(`${p.weight}kg`);
  if (p.width && p.height && p.length) {
    parts.push(`${p.width}×${p.height}×${p.length}`);
  }
  if (p.volume) parts.push(`${p.volume}m³`);
  return parts.join(' · ');
});

function handleSerialUpdate(value: string | null | undefined): void {
  emit('update:modelValue', {
    ...props.modelValue,
    serial: value ?? '',
  });
}

function handleDescriptionUpdate(value: string | null | undefined): void {
  emit('update:modelValue', {
    ...props.modelValue,
    description: value ?? null,
  });
}

function handlePiecesUpdate(value: number | null | undefined): void {
  emit('update:modelValue', {
    ...props.modelValue,
    pieces: value ?? 1,
  });
}

function handleQuantIdUpdate(value: string | null | undefined): void {
  emit('update:modelValue', {
    ...props.modelValue,
    quant_id: value ?? null,
  });
}
</script>

<template>
  <div :class="$style.jobEditProductRow" data-testid="jobEditProductRow">
    <ProductPrefixSelect
      :model-value="props.modelValue.prefix"
      :readonly="props.readonly"
      size="sm"
      @update:model-value="handlePrefixUpdate"
    />
    <div :class="$style.modelWrapper">
      <ProductModelLookup
        :model-value="props.modelValue.model"
        :readonly="props.readonly"
        :model-not-found="
          props.modelValue.model_id === null && !!props.modelValue.model
        "
        size="sm"
        @update:model-value="handleModelUpdate"
        @select-item="handleModelSelectItem"
      />
      <div v-if="dimensionsText" :class="$style.dimensionsText">
        {{ dimensionsText }}
      </div>
    </div>
    <KTextField
      :model-value="props.modelValue.serial"
      label="Serial"
      :readonly="props.readonly"
      size="sm"
      @update:model-value="handleSerialUpdate"
    />
    <KTextField
      :model-value="props.modelValue.description"
      label="Description"
      :readonly="props.readonly"
      size="sm"
      @update:model-value="handleDescriptionUpdate"
    />
    <KNumberField
      :model-value="props.modelValue.pieces"
      label="Quantity"
      :min="1"
      :readonly="props.readonly"
      size="sm"
      @update:model-value="handlePiecesUpdate"
    />
    <KTextField
      :model-value="props.modelValue.quant_id"
      label="Quant ID"
      :readonly="props.readonly"
      size="sm"
      @update:model-value="handleQuantIdUpdate"
    />
    <div :class="$style.pickingStatus">
      <PickingStatusChip
        v-if="props.modelValue.picking_status"
        :status="props.modelValue.picking_status"
      />
    </div>
    <div :class="$style.actions">
      <!-- :disabled, not :readonly: buttons have no readonly state -->
      <v-btn
        icon="mdi-delete-outline"
        size="small"
        variant="text"
        color="error"
        :disabled="props.readonly"
        @click="emit('delete', $event)"
      />
    </div>
  </div>
</template>

<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/grids';

.jobEditProductRow {
  display: grid;
  grid-template-columns: grids.$productsRowColumns;
  gap: 8px;
  align-items: start;
  padding: 8px 0 8px 8px;
}

.modelWrapper {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.dimensionsText {
  font-size: 0.6875rem;
  color: colors.$color-text-secondary;
  padding-left: 2px;
}

.pickingStatus {
  display: flex;
  align-items: center;
  min-height: 40px;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
